import Button from 'components/common/buttons/Button';
import { Lightning } from '@lightningjs/sdk';
import { constants } from 'aliases';
import { translate } from 'support/translate';
import { HoverableComponent } from 'components/common/HoverableComponent';
import ActionsList from 'components/common/ActionsList';
import { getFontFaceFromStyle } from 'support/textUtils';
import EmailInputField from '../common/EmailInputField';
import PasswordInputField from '../common/PasswordInputField';
import { LoginRadiusError } from 'types/loginRadius';
import TextLabel from 'components/common/TextLabel';

export interface SignInPasswordContentTemplateSpec
  extends Lightning.Component.TemplateSpec {
  email: string;
  fieldLevelError: LoginRadiusError;
  Title: typeof TextLabel;
  ActionsList: typeof ActionsList;
}

const EMAIL_REF = 'EmailInput';
const PASSWORD_REF = 'PasswordInput';
const CTA_ACTIONS_LIST = 'CtaActionsList';

const WIDTH = 684;
const SPACING = 16;

const TITLE_FONT_SIZE = 36;
const TITLE_OFFSET_BOTTOM = 28;
const BUTTON_WIDTH = 342;
const BUTTON_HEIGHT = 65;
const BUTTON_FONT_SIZE = 18;

const BUTTON_BASE = {
  type: Button,
  minWidth: BUTTON_WIDTH,
  height: BUTTON_HEIGHT,
  fontSize: BUTTON_FONT_SIZE,
  zIndex: constants.ui.zIndex.foregroundLayer + 2,
  signals: {
    $onHover: '$onHover',
  },
};

export default class SignInPasswordContent extends HoverableComponent<SignInPasswordContentTemplateSpec> {
  private _Title = this.getByRef('Title')!;
  private _ActionsList = this.getByRef('ActionsList')!;

  private _EmailInput = this._ActionsList.getItemByRef(
    EMAIL_REF,
  ) as EmailInputField;

  private _PasswordInput = this._ActionsList.getItemByRef(
    PASSWORD_REF,
  ) as PasswordInputField;

  private _CtaActionsList = this._ActionsList.getItemByRef(
    CTA_ACTIONS_LIST,
  ) as ActionsList;

  get email() {
    return this._EmailInput.input;
  }

  set email(value: string) {
    this._EmailInput.input = value;
  }

  get announce() {
    return [this._Title.text!.text];
  }

  set fieldLevelError(fieldLevelError: LoginRadiusError) {
    this._PasswordInput.loginFailedError = fieldLevelError;
  }

  static override _template(): Lightning.Component.Template<SignInPasswordContentTemplateSpec> {
    return {
      w: WIDTH,
      flex: {
        direction: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      },
      Title: {
        type: TextLabel,
        flexItem: { marginBottom: TITLE_OFFSET_BOTTOM },
        text: {
          text: translate('account.signInPassword.title'),
          textAlign: 'center',
          fontFace: getFontFaceFromStyle('bold'),
          fontSize: TITLE_FONT_SIZE,
        },
      },
      ActionsList: {
        flex: {
          direction: 'column',
        },
        w: WIDTH,
        type: ActionsList,
        spacing: SPACING,
        index: 1,
        passSignals: {
          $openKeyboard: true,
          $onForgotPassword: true,
          $resetToEmailInput: true,
        },
        signals: {
          $onHover: true,
          $onSignIn: true,
          $openKeyboard: true,
        },
        items: [
          {
            ref: EMAIL_REF,
            type: EmailInputField,
            passSignals: {
              $onHover: '$onHover',
              $emailInputSelected: '$resetToEmailInput',
            },
            signals: { $onHover: '$onHover' },
            zIndex: constants.ui.zIndex.foregroundLayer + 2,
          },
          {
            ref: PASSWORD_REF,
            type: PasswordInputField,
            passSignals: {
              $onHover: '$onHover',
              $openKeyboard: true,
            },
            signals: { $onHover: '$onHover' },
            zIndex: constants.ui.zIndex.foregroundLayer + 2,
            allowValidationError: false,
          },
          {
            type: ActionsList,
            ref: CTA_ACTIONS_LIST,
            flex: {
              direction: 'row',
            },
            spacing: SPACING,
            items: [
              {
                ...BUTTON_BASE,
                label: translate('account.signInPassword.signInButtonLabel'),
                action: '$onSignIn',
                passSignals: {
                  $onSignIn: true,
                },
              },
              {
                ...BUTTON_BASE,
                label: translate(
                  'account.signInPassword.forgotPasswordButtonLabel',
                ),
                action: '$onForgotPassword',
                passSignals: {
                  $onForgotPassword: true,
                },
              },
            ],
            signals: {
              $onHover: true,
            },
            passSignals: {
              $onSubmitRegistration: true,
              $onSignIn: true,
              $onForgotPassword: true,
            },
          },
        ],
      },
    };
  }

  override _getFocused() {
    return this._ActionsList;
  }

  private async $onSignIn(event: any) {
    this.signal('$onSignIn', this._EmailInput.input, this._PasswordInput.input);
  }

  private $onHover(target: any) {
    if (target === this._CtaActionsList || target === this._ActionsList) {
      this.signal('$onContentHovered');
    }
  }
}
